// Funding (SM-714): the node's pool (or, for a root, its master) in the
// selected currency, the clearing balance when the node has external
// credit, and the transfers the node is party to. Every figure is a
// ledger read made during the call. The pool transfer is offered with
// FUNDING_REQUEST on this node itself; user allocation is never offered.
//
//   GET  /api/broker/{id}/funding/pools?currency=                FundingPoolListResponse
//   GET  /api/broker/{id}/clearing?currency=                     BrokerClearingBalance
//   GET  /api/broker/{id}/funding/transfers?currency=&kind=&cursor=&limit=
//   POST /api/broker/{id}/funding/pool-transfers                 (PoolTransferDialog)
import { Link, createFileRoute } from "@tanstack/react-router";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { capabilityGuard, Guarded } from "@/components/layout/guard";
import { PageHeader } from "@/components/layout/PageHeader";
import { useCan, useScope } from "@/components/layout/ScopeContext";
import { SectionCard } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Money } from "@/components/ui/Money";
import { Id, KeyValues, TableFrame, Time } from "@/components/ui/Common";
import { byExchange } from "@/lib/actor";
import { EmptyState, ErrorState, LoadingState, QueryBlock } from "@/components/ui/States";
import { Select } from "@/components/ui/Field";
import { PoolTransferDialog, TransferFailure } from "@/components/PoolTransferDialog";
import { fetchClearing, fetchPools, fetchTransfers, keys } from "@/lib/queries";
import { FUNDING_TRANSFER_KINDS, type FundingPoolBalance } from "@/lib/types";
import { useI18n } from "@/i18n/I18nProvider";
import { labelOf } from "@/i18n/labels";

export const Route = createFileRoute("/_app/funding")({
  beforeLoad: capabilityGuard("FINANCE_VIEW"),
  component: FundingPage,
});

function FundingPage() {
  const { t } = useI18n();
  const { guard } = Route.useRouteContext();
  const { nodeId, currency } = useScope();
  const canTransfer = useCan("FUNDING_REQUEST");
  const [kind, setKind] = useState("");
  const [transferOpen, setTransferOpen] = useState(false);
  const probe = { queryKey: keys.pools(nodeId, currency), queryFn: () => fetchPools(nodeId, currency) };
  const poolsQ = useQuery({ ...probe, enabled: guard.kind === "allow" });
  const clearingQ = useQuery({
    queryKey: keys.clearing(nodeId, currency),
    queryFn: () => fetchClearing(nodeId, currency),
    enabled: guard.kind === "allow",
    retry: false,
  });
  const transfersQ = useInfiniteQuery({
    queryKey: keys.transfers(nodeId, { currency, kind: kind || undefined }),
    queryFn: ({ pageParam }) => fetchTransfers(nodeId, { currency, kind: kind || undefined, cursor: pageParam, limit: 25 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor || undefined,
    enabled: guard.kind === "allow",
  });
  const pool: FundingPoolBalance | undefined = (poolsQ.data?.pools ?? []).find((p) => p.currency === currency);

  return (
    <Guarded guard={guard} probe={probe}>
      <PageHeader
        title={t("funding.title")}
        subtitle={t("funding.subtitle")}
        coverage="node"
        money
        actions={
          canTransfer ? (
            <Button onClick={() => setTransferOpen(true)} disabled={!pool}>
              {t("transfer.open")}
            </Button>
          ) : undefined
        }
      />
      {!canTransfer && <p className="banner mb-4 text-xs">{t("funding.transferNeedsRole")}</p>}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <SectionCard title={t("funding.pool")} className="xl:col-span-2">
          <QueryBlock
            query={poolsQ}
            empty={() => (!pool ? <EmptyState title={t("funding.noPool", { currency })} hint={t("funding.noPoolHint")} /> : null)}
          >
            {() =>
              pool ? (
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone="accent">{t(`fundingRole.${pool.role}`)}</Badge>
                    <span className="mono text-2xs text-t-3">{pool.account_code}</span>
                    <Badge tone={pool.status === "ACTIVE" ? "yes" : "warn"}>{labelOf(t, "poolStatus", pool.status)}</Badge>
                  </div>
                  <KeyValues
                    rows={[
                      { label: t("funding.available"), value: <Money currency={currency} value={pool.available} /> },
                      { label: t("funding.reserved"), value: <Money currency={currency} value={pool.reserved} /> },
                      { label: t("funding.pendingWithdrawal"), value: <Money currency={currency} value={pool.pending_withdrawal} /> },
                      {
                        label: t("funding.canSpendReceive"),
                        value: t("funding.canSpendReceiveValue", {
                          spend: pool.can_spend ? t("common.yes") : t("common.no"),
                          receive: pool.can_receive ? t("common.yes") : t("common.no"),
                        }),
                      },
                    ]}
                  />
                  <p className="text-2xs text-t-3">
                    {t("common.asOf")} <Time ms={poolsQ.data?.as_of_unix_ms} />
                  </p>
                </div>
              ) : null
            }
          </QueryBlock>
        </SectionCard>
        <SectionCard title={t("funding.clearing")}>
          {clearingQ.isLoading ? (
            <LoadingState lines={2} />
          ) : clearingQ.error ? (
            <ErrorState error={clearingQ.error} compact />
          ) : clearingQ.data && clearingQ.data.exists ? (
            <div className="space-y-2 text-sm">
              <p className="text-lg font-semibold">
                <Money currency={currency} value={clearingQ.data.balance} tone="auto" />
              </p>
              <p className="text-xs text-t-2">{t(`clearing.direction.${clearingQ.data.direction}`)}</p>
              <p className="text-xs text-t-3">
                {t("clearing.owed")} <Money currency={currency} value={clearingQ.data.owed} />
              </p>
              {/* the raw ledger figure: settlements never post to the ledger (SM-875, SM-941) */}
              <p className="text-2xs text-t-3">{t("clearing.beforeSettlements")}</p>
              <Link to="/settlement" search={{ node: nodeId } as never} className="text-2xs font-semibold text-neon hover:underline">
                {t("clearing.seeSettlement")}
              </Link>
            </div>
          ) : (
            <p className="text-sm text-t-3">{t("clearing.none")}</p>
          )}
        </SectionCard>
      </div>

      <SectionCard
        title={t("funding.transfers")}
        className="mt-4"
        actions={
          <div className="w-52">
            <Select
              label={t("funding.kind")}
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              options={[{ value: "", label: t("common.all") }, ...FUNDING_TRANSFER_KINDS.map((k) => ({ value: k, label: t(`transferKind.${k}`) }))]}
            />
          </div>
        }
      >
        {transfersQ.isLoading ? (
          <LoadingState />
        ) : transfersQ.error ? (
          <ErrorState error={transfersQ.error} onRetry={() => void transfersQ.refetch()} />
        ) : (transfersQ.data?.pages.flatMap((p) => p.items ?? []) ?? []).length === 0 ? (
          <EmptyState title={t("funding.noTransfers")} />
        ) : (
          <>
            <TableFrame label={t("funding.transfers")}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th scope="col">{t("common.when")}</th>
                    <th scope="col">{t("funding.kind")}</th>
                    <th scope="col">{t("common.status")}</th>
                    <th scope="col">{t("funding.fromTo")}</th>
                    <th scope="col" className="text-right">
                      {t("funding.amount")}
                    </th>
                    <th scope="col">{t("funding.actor")}</th>
                    <th scope="col">{t("funding.operation")}</th>
                  </tr>
                </thead>
                <tbody>
                  {transfersQ.data?.pages
                    .flatMap((p) => p.items ?? [])
                    .map((x) => (
                      <tr key={x.transfer_id}>
                        <td className="text-xs">
                          <Time ms={x.created_at_unix_ms} />
                        </td>
                        <td className="text-xs">{t(`transferKind.${x.kind}`)}</td>
                        <td>
                          <Badge tone={x.state === "COMMITTED" ? "yes" : x.state === "FAILED" ? "no" : "warn"}>
                            {t(`transferState.${x.state}`)}
                          </Badge>
                          {x.failure_code && <TransferFailure code={x.failure_code} className="mt-1 max-w-56 text-2xs" />}
                        </td>
                        <td className="text-xs">
                          <Id value={x.source_broker_id || x.broker_account_id} /> → <Id value={x.destination_broker_id || x.broker_account_id} />
                        </td>
                        <td className="text-right">
                          <Money currency={x.currency} value={x.amount} />
                        </td>
                        <td className="text-xs">
                          {byExchange(x.actor_id, x.actor_kind) ? (
                            t("actor.exchange")
                          ) : (
                            <>
                              {t(`actorKind.${x.actor_kind}`)} <Id value={x.actor_id} />
                            </>
                          )}
                        </td>
                        <td>
                          <Id value={x.operation_id} />
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </TableFrame>
            {transfersQ.hasNextPage && (
              <div className="mt-3">
                <Button variant="ghost" size="sm" loading={transfersQ.isFetchingNextPage} onClick={() => void transfersQ.fetchNextPage()}>
                  {t("common.loadMore")}
                </Button>
              </div>
            )}
          </>
        )}
      </SectionCard>

      {transferOpen && (
        <PoolTransferDialog nodeId={nodeId} currency={currency} available={pool?.available} onClose={() => setTransferOpen(false)} />
      )}
    </Guarded>
  );
}
